/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Carousel,
  DatePicker,
  Descriptions,
  Image,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  TableColumnProps,
  Tag,
  Typography
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { get, post } from '../requestNew';
import './index.scss';
import type {
  HoldStock,
  OrderDetail,
  PenaltyCancelApplyItem,
  PenaltyCancelApplyOrderDetail
} from './types';

const Option = Select.Option;
const TextArea = Input.TextArea;

const PENALTY_TYPE_MAP: Record<number, { label: string; color: string }> = {
  1: { label: '黄牌', color: 'orangered' },
  2: { label: '红牌', color: 'red' }
};

const STATUS_MAP: Record<number, { label: string; color: string }> = {
  0: { label: '待审核', color: 'gold' },
  1: { label: '已通过', color: 'green' },
  2: { label: '已驳回', color: 'red' }
};

const DEFAULT_PAGE_SIZE = 20;

function urlProcess(url: string) {
  if (url.indexOf('?x-oss-process') > 0) {
    return url.split('?x-oss-process')[0];
  }
  return url;
}

function PenaltyCancelApply() {
  /** 工作台跳转带过来的赛季与状态，每次进入页面时读取 */
  const initialQuery = useMemo(() => new URLSearchParams(window.location.search), []);
  const [gameSession, setGameSession] = useState(initialQuery.get('gameSession') || '');
  const [sessionList, setSessionList] = useState<Array<{ label: string; value: string }>>([]);
  const [status, setStatus] = useState<number | undefined>(() => {
    const value = initialQuery.get('status');
    if (value === '0' || value === '1' || value === '2') return Number(value);
    return 0;
  });
  const [holdDateFilter, setHoldDateFilter] = useState('');
  const [gameNameFilter, setGameNameFilter] = useState('');
  const [openIdFilter, setOpenIdFilter] = useState('');

  const [dataList, setDataList] = useState<PenaltyCancelApplyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [reviewVisible, setReviewVisible] = useState(false);
  const [reviewRecord, setReviewRecord] = useState<PenaltyCancelApplyItem | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewRemark, setReviewRemark] = useState('');

  const [orderVisible, setOrderVisible] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState<PenaltyCancelApplyOrderDetail | null>(null);

  const getGameSessionList = () => {
    get('/gameConfig/getGameSessionList')
      .then((res: any) => {
        const { data } = res || {};
        if (data && Array.isArray(data) && data.length > 0) {
          const options = data.map((item: any) => ({ label: item, value: item }));
          const sorted = options.sort((a: any, b: any) => {
            const aVal = String(a.value);
            const bVal = String(b.value);
            const aNum = /^\d+$/.test(aVal);
            const bNum = /^\d+$/.test(bVal);
            if (aNum && bNum) return parseInt(bVal, 10) - parseInt(aVal, 10);
            if (aNum && !bNum) return -1;
            if (!aNum && bNum) return 1;
            return aVal.localeCompare(bVal);
          });
          setSessionList(sorted);
          const urlSession = initialQuery.get('gameSession');
          const defaultSession =
            urlSession && sorted.some((option: any) => String(option.value) === urlSession)
              ? urlSession
              : sorted[0].value;
          setGameSession(defaultSession);
          getList({ gameSession: defaultSession, pageNum: 1 });
        } else {
          getList();
        }
      })
      .catch(() => {
        Message.error('获取赛季列表失败');
        getList();
      });
  };

  const getList = (opts: Record<string, any> = {}) => {
    setLoading(true);
    const params: any = {
      gameSession: opts.gameSession !== undefined ? opts.gameSession : gameSession,
      openId: openIdFilter.trim() || undefined,
      status: opts.status !== undefined ? opts.status : status,
      pageNum: opts.pageNum !== undefined ? opts.pageNum : pageNum,
      pageSize: opts.pageSize !== undefined ? opts.pageSize : pageSize
    };

    const dateStr = opts.holdDate !== undefined ? opts.holdDate : holdDateFilter;
    if (typeof dateStr === 'string' && dateStr.trim()) {
      params.holdDate = dateStr.trim();
    }
    if (gameNameFilter.trim()) {
      params.gameName = gameNameFilter.trim();
    }

    get('/sys/umsPenaltyCancelApply/list', params)
      .then((res: any) => {
        if (res?.code === 200) {
          const data = res?.data ?? {};
          const list = data.list ?? data.records ?? [];
          setDataList(list);
          setTotal(data.total ?? 0);
        } else {
          Message.error(res?.message || '查询失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getGameSessionList();
  }, []);

  useEffect(() => {
    if (sessionList.length === 0) return;
    getList();
  }, [pageNum, pageSize]);

  const openReviewModal = (record: PenaltyCancelApplyItem, action: 'approve' | 'reject') => {
    setReviewRecord(record);
    setReviewAction(action);
    setReviewRemark('');
    setReviewVisible(true);
  };

  const viewOrder = (record: PenaltyCancelApplyItem) => {
    setOrderVisible(true);
    setOrderLoading(true);
    setOrderDetail(null);
    get('/sys/umsPenaltyCancelApply/getOrderDetail', { id: record.id })
      .then((res: any) => {
        if (res?.code === 200) {
          setOrderDetail(res.data ?? null);
        } else {
          Message.error(res?.message || '获取报单失败');
          setOrderVisible(false);
        }
      })
      .catch(() => {
        Message.error('网络错误');
        setOrderVisible(false);
      })
      .finally(() => setOrderLoading(false));
  };

  const closeOrderModal = () => {
    setOrderVisible(false);
    setOrderDetail(null);
  };

  const submitReview = () => {
    if (!reviewRecord?.id) return;
    const url = reviewAction === 'approve'
      ? '/sys/umsPenaltyCancelApply/approve'
      : '/sys/umsPenaltyCancelApply/reject';
    post(url, { id: reviewRecord.id, reviewRemark: reviewRemark.trim() }).then((res: any) => {
      if (res?.code === 200) {
        Message.success(reviewAction === 'approve' ? '已通过申请' : '已驳回申请');
        setReviewVisible(false);
        getList();
      } else {
        Message.error(res?.message || '操作失败');
      }
    }).catch(() => Message.error('网络错误'));
  };

  const renderOrderContent = (order: OrderDetail) => (
    <>
      <Descriptions
        column={2}
        size="small"
        style={{ marginBottom: 16 }}
        data={[
          { label: '参赛名', value: order.gameName || '-' },
          { label: '持仓日期', value: order.holdDate || '-' },
          { label: '今日总资产', value: order.totalAssets ?? '-' },
          { label: '今日收益率', value: order.todayEarningRate != null ? `${order.todayEarningRate}%` : '-' },
          { label: '总收益率', value: order.totalEarningRate != null ? `${order.totalEarningRate}%` : '-' },
          { label: '仓位', value: order.stockPercent != null ? `${order.stockPercent}%` : '-' },
          { label: '今日盈亏', value: order.todayProfitLoss ?? '-' }
        ]}
      />
      {order.orderPicVos && order.orderPicVos.length > 0 && (
        <Carousel
          autoPlay={false}
          animation="card"
          indicatorPosition="outer"
          className="order-detail-carousel"
          style={{ width: '100%', background: '#f5f5f5', marginBottom: 16 }}
        >
          {order.orderPicVos.map((item) => (
            <div key={item.id} className="order-pic-75" style={{ textAlign: 'center' }}>
              <Image src={urlProcess(item.url)} width="100%" preview />
            </div>
          ))}
        </Carousel>
      )}
      {order.holdStockVos && order.holdStockVos.length > 0 && (
        <Table
          rowKey={(record) => `${record.stockName || ''}-${record.holdPrice ?? ''}`}
          size="small"
          pagination={false}
          columns={[
            { title: '股票名称', dataIndex: 'stockName' },
            { title: '持仓(元)', dataIndex: 'holdPrice' }
          ]}
          data={order.holdStockVos as HoldStock[]}
        />
      )}
    </>
  );

  const renderNotebookContent = () => {
    const notebook = orderDetail?.notebook;
    if (!notebook) return null;
    return (
      <div className="order-notebook-fallback">
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          未找到报单数据，展示关联笔记：
        </Typography.Text>
        {notebook.title && (
          <Typography.Text bold style={{ display: 'block', marginBottom: 8 }}>
            {notebook.title}
          </Typography.Text>
        )}
        {notebook.content && (
          <Typography.Text style={{ display: 'block', marginBottom: 12, lineHeight: 1.6 }}>
            {notebook.content}
          </Typography.Text>
        )}
        {notebook.picture && Array.isArray(notebook.picture) && notebook.picture.length > 0 && (
          <div className="order-notebook-pics">
            {notebook.picture.map((pic, index) => (
              <div key={index} className="order-pic-75" style={{ marginRight: 8, marginBottom: 8 }}>
                <Image src={pic} width={200} preview />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const columns: TableColumnProps<PenaltyCancelApplyItem>[] = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '参赛名', dataIndex: 'gameName', width: 110 },
    { title: 'openId', dataIndex: 'openId', width: 180, ellipsis: true },
    { title: '赛季', dataIndex: 'gameSession', width: 80 },
    { title: '处罚日期', dataIndex: 'holdDate', width: 110 },
    {
      title: '处罚类型',
      dataIndex: 'penaltyType',
      width: 90,
      render: (val: number) => {
        const item = PENALTY_TYPE_MAP[val];
        return item ? <Tag color={item.color}>{item.label}</Tag> : '-';
      }
    },
    { title: '处罚原因', dataIndex: 'penaltyReason', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (val: number) => {
        const item = STATUS_MAP[val];
        return item ? <Tag color={item.color}>{item.label}</Tag> : '-';
      }
    },
    { title: '申请时间', dataIndex: 'createTime', width: 160 },
    { title: '审核人', dataIndex: 'reviewer', width: 90 },
    { title: '审核备注', dataIndex: 'reviewRemark', ellipsis: true },
    {
      title: '操作',
      width: 240,
      fixed: 'right',
      render: (_: unknown, record: PenaltyCancelApplyItem) => (
        <Space>
          <Button type="text" onClick={() => viewOrder(record)}>
            查看报单
          </Button>
          {record.status === 0 && (
            <>
              <Button type="text" status="success" onClick={() => openReviewModal(record, 'approve')}>
                通过
              </Button>
              <Button type="text" status="danger" onClick={() => openReviewModal(record, 'reject')}>
                驳回
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="penalty-cancel-apply-page table-flex">
      <Typography.Title heading={5} style={{ marginTop: 0, flexShrink: 0 }}>红黄牌取消申请</Typography.Title>
      <div className="filter-bar">
        <Select
          className="filter-item"
          placeholder="赛季"
          style={{ width: 120 }}
          value={gameSession}
          onChange={(val) => {
            setGameSession(val);
            setPageNum(1);
            getList({ gameSession: val, pageNum: 1 });
          }}
        >
          {sessionList.map((item) => (
            <Option key={item.value} value={item.value}>{item.label}</Option>
          ))}
        </Select>
        <Select
          className="filter-item"
          placeholder="状态"
          allowClear
          style={{ width: 120 }}
          value={status}
          onChange={(val) => {
            setStatus(val);
            setPageNum(1);
            getList({ status: val, pageNum: 1 });
          }}
        >
          <Option value={0}>待审核</Option>
          <Option value={1}>已通过</Option>
          <Option value={2}>已驳回</Option>
        </Select>
        <DatePicker
          className="filter-item"
          placeholder="处罚日期"
          style={{ width: 140 }}
          value={holdDateFilter ? dayjs(holdDateFilter) : undefined}
          onChange={(val: any) => {
            const dateStr = val ? dayjs(val).format('YYYY-MM-DD') : '';
            setHoldDateFilter(dateStr);
            setPageNum(1);
            getList({ holdDate: dateStr, pageNum: 1 });
          }}
          allowClear
        />
        <Input
          className="filter-item"
          placeholder="参赛名"
          style={{ width: 140 }}
          value={gameNameFilter}
          onChange={setGameNameFilter}
          allowClear
        />
        <Input
          className="filter-item"
          placeholder="openId"
          style={{ width: 160 }}
          value={openIdFilter}
          onChange={setOpenIdFilter}
          allowClear
        />
        <Button
          type="primary"
          className="filter-query-btn"
          onClick={() => { setPageNum(1); getList({ pageNum: 1 }); }}
        >
          查询
        </Button>
      </div>

      <div className="table-content">
        <Spin loading={loading} style={{ width: '100%' }}>
          <Table
            rowKey="id"
            columns={columns}
            data={dataList}
            className="penalty-cancel-table"
            scroll={{ x: '100%' }}
            pagination={{
              current: pageNum,
              pageSize,
              total,
              showTotal: true,
              sizeCanChange: true,
              onChange: (page, size) => {
                setPageNum(page);
                setPageSize(size);
              }
            }}
          />
        </Spin>
      </div>

      <Modal
        title={reviewAction === 'approve' ? '通过取消申请' : '驳回取消申请'}
        visible={reviewVisible}
        onCancel={() => setReviewVisible(false)}
        onOk={submitReview}
        okText={reviewAction === 'approve' ? '确认通过' : '确认驳回'}
        okButtonProps={{ status: reviewAction === 'approve' ? 'success' : 'danger' }}
      >
        <div style={{ marginBottom: 12 }}>
          {reviewRecord?.gameName} / {reviewRecord?.holdDate} /
          {PENALTY_TYPE_MAP[reviewRecord?.penaltyType || 0]?.label || ''}
          {reviewRecord && (
            <Button type="text" size="small" style={{ marginLeft: 8 }} onClick={() => viewOrder(reviewRecord)}>
              查看报单
            </Button>
          )}
        </div>
        <TextArea
          placeholder="审核备注（可选）"
          value={reviewRemark}
          onChange={setReviewRemark}
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </Modal>

      <Modal
        title="报单详情"
        visible={orderVisible}
        style={{ width: 720 }}
        footer={null}
        onCancel={closeOrderModal}
        unmountOnExit
      >
        <Spin loading={orderLoading} style={{ width: '100%' }}>
          {!orderLoading && orderDetail?.orderDetails && renderOrderContent(orderDetail.orderDetails)}
          {!orderLoading && !orderDetail?.orderDetails && renderNotebookContent()}
        </Spin>
      </Modal>
    </div>
  );
}

export default PenaltyCancelApply;
